import type { FC, ReactNode } from 'react'
import type { ProductImage } from '@/types'

import Image from 'next/image'
import { useState } from 'react'
import { cn } from '@/lib/utils'

interface ProductGalleryProps {
    images: Array<ProductImage>
    name: string
}

const ProductGallery: FC<ProductGalleryProps> = ({ images, name }): ReactNode => {
    const [current, setCurrent] = useState(0)

    return (
        <div className='flex flex-col gap-5 md:flex-row-reverse md:items-start md:gap-6'>
            <div className='paper-lift flex-1'>
                <div className='deckle relative aspect-[4/5] overflow-hidden bg-paper-deep'>
                    {images.map((image, index) => (
                        <div
                            key={`${image.src}-${index}`}
                            data-active={index === current}
                            aria-hidden={index !== current}
                            className='story-sheet absolute inset-0'
                        >
                            <Image
                                src={image.src}
                                alt={image.alt}
                                fill
                                priority={index === 0}
                                sizes='(min-width: 1024px) 50vw, 92vw'
                                quality={85}
                                className='object-cover'
                            />
                        </div>
                    ))}
                </div>
            </div>

            {images.length > 1 && (
                <ul aria-label={`Photos of ${name}`} className='flex gap-3 md:w-20 md:flex-col lg:w-24'>
                    {images.map((image, index) => (
                        <li key={`${image.src}-thumb-${index}`} className='w-[22%] max-w-24 md:w-full'>
                            <button
                                type='button'
                                onClick={() => setCurrent(index)}
                                aria-pressed={index === current}
                                aria-label={`Show photo ${index + 1} of ${images.length}`}
                                className={cn(
                                    'relative block aspect-[4/5] w-full overflow-hidden rounded-[2px] bg-paper-deep transition-opacity duration-500',
                                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bark',
                                    index === current ? 'opacity-100 ring-1 ring-bark/50 ring-offset-2 ring-offset-paper' : 'opacity-60 hover:opacity-100'
                                )}
                            >
                                <Image src={image.src} alt='' fill sizes='96px' className='object-cover' />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default ProductGallery
